/**
 * The notification catalogue — P4, ADR-0078.
 *
 * **Code decides what can notify; the dashboard decides what does** (ADR-0078 §1). Which event may
 * tell whom about what is a decision about I1 — an audience, and the fields that audience may see —
 * so it is stated here, once, where a reviewer reads every message the platform can send in one
 * file. A type added in a later milestone notifies nobody on the commit that introduces it unless
 * its entry here says so.
 *
 * The API's planners read this catalogue for each type's trigger and paging reasons, the database
 * CHECKs on `notification_sends` are pinned to these lists by a test, and the console's copy will be
 * held to them by the i18n coverage gate when the dashboard names them (P4-3).
 */
import { z } from 'zod';
import { OPS_FACETS } from './principals.js';
import { renderedText } from './rendered-text.js';
/**
 * How a notification reaches a person (ADR-0078 §4). **A channel joins this list when its sender
 * exists**, not before: a rule offering a channel nothing delivers on would read as configured and
 * reach nobody. `messaging` is WhatsApp then SMS through the `MessagingProvider` port, one channel
 * because §8's order lives inside the port. `in_app` is the bell (P4-2): an inbox item routed to
 * facets. `ops_webhook`, `web_push` and `courier_push` join in P4-4, P4-5 and P4-6.
 */
export const NOTIFICATION_CHANNELS = ['messaging', 'in_app'];
/**
 * The channels whose text Tard composes, and so the only channels a template is written for (P4-3b,
 * ADR-0078 §1). `messaging` carries a sentence a person reads outside the console. The bell is not
 * here: the console draws an item in its reader's language from its own copy, and an item is routed
 * to facets rather than to one reader, so a text frozen in one language at send time would undo §8.
 * A push carries no text at all (§5).
 */
export const TEMPLATED_CHANNELS = ['messaging'];
/** The languages a notice is written in: §8's three, Arabic first. */
export const NOTIFICATION_LOCALES = ['ar', 'en', 'fr'];
/**
 * The languages tried, in order, for a send in `locale` (CLAUDE.md §8): French falls back to English,
 * then Arabic; English to Arabic. Arabic is the floor.
 */
export function localeFallback(locale) {
    return locale === 'fr' ? ['fr', 'en', 'ar'] : locale === 'en' ? ['en', 'ar'] : ['ar'];
}
/**
 * Who a notification is for: the `MessagingProvider` port's own recipients, which each map to a
 * viewer role whose allow-list the variables are projected through (`assertSendable`).
 */
export const NOTIFICATION_AUDIENCES = ['buyer', 'seller', 'courier', 'ops'];
/** How urgent a type is: what a person may mute (not `critical`) and how the bell orders it. */
export const NOTIFICATION_SEVERITIES = ['critical', 'high', 'normal'];
/**
 * What a send is about: a minted public id of one of these kinds (ADR-0068). The kernel's
 * `MessageSubjectKind` is the same three, and a test in the API holds the two together; contracts
 * cannot import the kernel.
 */
export const NOTIFICATION_SUBJECT_KINDS = ['order', 'invoice', 'merchant'];
/**
 * A send's state, as `notification_sends_status` admits it. `refused` is the send scan's answer —
 * our data, never retried; `abandoned` is a provider that kept failing past the last attempt.
 */
export const NOTIFICATION_SEND_STATUSES = ['pending', 'sent', 'refused', 'abandoned'];
/**
 * Why a send was not sent, as a code of ours. A provider's own error text may echo what it was
 * sent, so it goes to the redacted log and never into a column a screen renders (ADR-0078 §2).
 */
export const NOTIFICATION_ERROR_CODES = [
    'provider_error',
    'provider_unavailable',
    'send_refused',
    'subject_refused',
];
/**
 * What an Ops alert carries (`OpsAlertsService`): the subject's reference under the name its kind is
 * read by, the Merchant's display name, and the event and reason that caused it.
 */
const ORDER_ALERT_VARIABLES = ['orderReference', 'merchantName', 'eventType', 'reason'];
const MERCHANT_ALERT_VARIABLES = ['merchantId', 'merchantName', 'eventType', 'reason'];
/**
 * Every notification the platform can send. The key is also the `templateId` handed to the port, so
 * a provider's template is named by the type.
 *
 * The six that existed before the catalogue, with their behaviour unchanged (ADR-0078 §1): the
 * Buyer's out-for-delivery notice (M6 slice C), and the Ops alerts on the five events the platform
 * deliberately refused to resolve (T2-6, ADR-0068, ADR-0075).
 */
export const NOTIFICATION_TYPES = {
    'buyer.out_for_delivery': {
        trigger: 'leg.delivery.started',
        audience: 'buyer',
        subjectKind: 'order',
        severity: 'normal',
        channels: ['messaging'],
        variables: ['merchantDisplayName', 'orderReference', 'courierFirstName', 'courierVehicle', 'maskedChannelId'],
        opensMaskedChannel: true,
    },
    // Two records disagree about where a parcel is; ingest halted for a person (ADR-0049).
    'ops.custody_conflict': {
        trigger: 'ops.custody_conflict',
        audience: 'ops',
        subjectKind: 'order',
        severity: 'critical',
        channels: ['messaging', 'in_app'],
        variables: ORDER_ALERT_VARIABLES,
        // An adjudicator rules on it (ADR-0049); platform staff are told of every critical exception.
        inbox: { facets: ['adjudicator'], platformStaff: true },
    },
    // Risk 1's freeze. §6's custody clock keeps running on purpose, so it costs while nobody looks.
    'ops.custody_frozen': {
        trigger: 'cod.custody_frozen',
        audience: 'ops',
        subjectKind: 'order',
        severity: 'critical',
        channels: ['messaging', 'in_app'],
        variables: ORDER_ALERT_VARIABLES,
        // Compliance froze it and releases it; finance accounts for the money held meanwhile.
        inbox: { facets: ['compliance', 'finance'], platformStaff: true },
    },
    // §5's promise, already broken. The at-risk sweep warns before this; this is the record.
    'ops.sla_breached': {
        trigger: 'sla.breached',
        audience: 'ops',
        subjectKind: 'order',
        severity: 'high',
        channels: ['messaging', 'in_app'],
        variables: ORDER_ALERT_VARIABLES,
        // No facet owns a breach: dispatch and the promise are platform staff's (ADR-0078 §6).
        inbox: { facets: [], platformStaff: true },
    },
    // I7's finding. The cadence writes one per Merchant per tick and almost all say `clear`.
    'ops.merchant_screening_not_clear': {
        trigger: 'merchant.screened',
        reasons: ['screening_review', 'screening_hit'],
        audience: 'ops',
        subjectKind: 'merchant',
        severity: 'high',
        channels: ['messaging', 'in_app'],
        variables: MERCHANT_ALERT_VARIABLES,
        inbox: { facets: ['compliance'], platformStaff: false },
    },
    // A person's ruling (SC3, ADR-0075): a match recorded, and a match lifted, both page.
    'ops.merchant_screening_adjudicated': {
        trigger: 'merchant.screening_adjudicated',
        reasons: ['hit_recorded', 'hit_lifted'],
        audience: 'ops',
        subjectKind: 'merchant',
        severity: 'normal',
        channels: ['messaging', 'in_app'],
        variables: MERCHANT_ALERT_VARIABLES,
        inbox: { facets: ['compliance'], platformStaff: false },
    },
};
/** The catalogue's keys, in the order above. */
export const NOTIFICATION_TYPE_KEYS = Object.keys(NOTIFICATION_TYPES);
/**
 * The types an event may cause, given its reason code. A pure lookup over the catalogue, so the
 * planners and the tests agree on which types an event triggers without restating the map.
 */
export function notificationTypesFor(eventType, reasonCode) {
    return NOTIFICATION_TYPE_KEYS.filter((key) => {
        const spec = NOTIFICATION_TYPES[key];
        if (spec.trigger !== eventType)
            return false;
        return spec.reasons === undefined || (reasonCode !== null && spec.reasons.includes(reasonCode));
    });
}
/** Whether any catalogue type is triggered by this event, before its reason is known. */
export function notifiesOn(eventType) {
    return NOTIFICATION_TYPE_KEYS.some((key) => NOTIFICATION_TYPES[key].trigger === eventType);
}
/**
 * Why a mute was not recorded. Each names one thing a person can see on their own settings screen.
 *
 * There is deliberately no code for *a notice that does not reach you*: a mute is a standing
 * preference, so muting a notice before gaining the facet that receives it is harmless and becomes
 * meaningful the moment the facet arrives. Refusing it would be a guard with nothing to protect that
 * breaks whenever somebody's responsibilities change.
 */
export const INBOX_MUTE_ISSUE_CODES = [
    // A critical notice: custody contested, custody frozen. Nobody may stop their own bell ringing
    // for one, so it is refused here as well as declined at the read.
    'type_is_critical',
    // A notice that is not rung in the bell at all, so a mute would mean nothing. A Buyer's
    // out-for-delivery notice is the one in the catalogue today.
    'type_not_in_bell',
];
// ---------------------------------------------------------------------------
// The rules: what the dashboard decides (P4-3, ADR-0078 §1)
// ---------------------------------------------------------------------------
/**
 * Why a rule was not saved. Each names one thing on the form, and the console says it in words.
 */
export const NOTIFICATION_RULE_ISSUE_CODES = [
    // A channel the catalogue does not send this type on: a rule may switch channels off, never invent one.
    'channel_not_allowed',
    // Switched on and sent on nothing: a rule that reads as on and reaches nobody.
    'channels_empty',
    // Rung in the bell and routed to nobody: an item nobody could ever see.
    'routing_required',
    // A routing for a type not sent in the bell, which nothing would read.
    'routing_not_allowed',
    // Nothing moved: an event recording an amendment that did not happen is a record nothing corrects.
    'rule_unchanged',
];
/**
 * What a rule change did, ranked so the event's reason names the most consequential move: switching a
 * notice off first, because it is the one that stops somebody being told.
 */
export const NOTIFICATION_RULE_REASONS = [
    'rule_disabled',
    'rule_enabled',
    'channels_changed',
    'routing_changed',
    'severity_changed',
    'ownership_changed',
];
const reviewDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'review_date_format');
const facetSet = z
    .array(z.enum(OPS_FACETS))
    .max(OPS_FACETS.length)
    .refine((facets) => new Set(facets).size === facets.length, { message: 'a facet may appear once' });
/**
 * A rule, whole. Every field is sent every time, as the facet route sends a whole set: a partial body
 * invites two saves that disagree about what the operator meant.
 */
export const amendNotificationRuleSchema = z
    .object({
    enabled: z.boolean(),
    channels: z
        .array(z.enum(NOTIFICATION_CHANNELS))
        .max(NOTIFICATION_CHANNELS.length)
        .refine((channels) => new Set(channels).size === channels.length, { message: 'a channel may appear once' }),
    severity: z.enum(NOTIFICATION_SEVERITIES),
    /** Who sees it in the bell. Null for a type not sent `in_app`. */
    inbox: z.object({ facets: facetSet, platformStaff: z.boolean() }).strict().nullable(),
    owner: z.string().trim().min(1).max(200),
    reviewDate,
    note: z.string().trim().min(1).max(500),
})
    .strict();
export const amendNotificationRuleRendered = renderedText(amendNotificationRuleSchema, {
    prose: ['owner'],
    exempt: {
        enabled: 'a boolean',
        channels: 'each a member of NOTIFICATION_CHANNELS, checked by the schema',
        severity: 'a member of NOTIFICATION_SEVERITIES, checked by the schema',
        inbox: 'facets from OPS_FACETS and a boolean, checked by the schema',
        reviewDate: 'a YYYY-MM-DD date (review_date_format), which holds no phone-shaped digit run',
        note: 'stored only in the config.notification_rules.amended event payload; no view renders it',
    },
});
// ---------------------------------------------------------------------------
// The templates: what a notice says (P4-3b, ADR-0078 §1 and §5)
// ---------------------------------------------------------------------------
/**
 * A placeholder: `{{name}}`, spaces allowed inside the braces, a name of letters and digits starting
 * with a letter. One definition for the save check, the screen's preview and the send's render, so
 * the three cannot disagree about what a template names.
 */
const PLACEHOLDER = /\{\{\s*([A-Za-z][A-Za-z0-9]*)\s*\}\}/gu;
/**
 * The values a template names, once each, and whether it holds braces that are not a placeholder: a
 * `{{` or `}}` left after every placeholder is taken out is a typo that would reach a person as
 * literal braces.
 */
export function templatePlaceholders(body) {
    const names = [...body.matchAll(PLACEHOLDER)].map((match) => match[1]);
    const rest = body.replace(PLACEHOLDER, '');
    return { names: [...new Set(names)], malformed: rest.includes('{{') || rest.includes('}}') };
}
/**
 * A template with its values put in. A value that is absent, or not a string or a number, renders as
 * nothing: the values handed in are the projected ones, so a name the audience may not see has
 * already been taken out and must not come back as a hole the reader can see through.
 */
export function renderTemplate(body, variables) {
    return body.replace(PLACEHOLDER, (_whole, name) => {
        const value = variables[name];
        return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
    });
}
/** Why a template was not saved. Each names one thing on the form, and the console says it in words. */
export const NOTIFICATION_TEMPLATE_ISSUE_CODES = [
    // A channel this notice is not sent on as text: the bell and push carry no template.
    'channel_not_templated',
    // A placeholder naming a value this notice does not carry, or its audience may not see.
    'placeholder_unknown',
    // A `{{` or `}}` that is not part of a placeholder.
    'placeholder_malformed',
    // Somebody saved this language's text since the editor opened it: re-read before saving over it.
    'template_moved',
    // The same text as the version in force: nothing to record.
    'template_unchanged',
];
/** What a template save did: the first text in a language, or a new version of one. */
export const NOTIFICATION_TEMPLATE_REASONS = ['template_written', 'template_revised'];
/** The longest text a template may hold: within a WhatsApp template body's limit, with room. */
export const NOTIFICATION_TEMPLATE_MAX_LENGTH = 1000;
/**
 * One language's text, as a new version. `basedOn` is the version the editor opened (null when the
 * language had none), so a save over somebody else's newer text is refused rather than lost.
 */
export const recordNotificationTemplateSchema = z
    .object({
    channel: z.enum(NOTIFICATION_CHANNELS),
    locale: z.enum(NOTIFICATION_LOCALES),
    basedOn: z.number().int().min(1).nullable(),
    body: z.string().trim().min(1).max(NOTIFICATION_TEMPLATE_MAX_LENGTH),
    note: z.string().trim().min(1).max(500),
})
    .strict();
export const recordNotificationTemplateRendered = renderedText(recordNotificationTemplateSchema, {
    // The text a person reads: an operator must not be able to type a phone number into it (§5).
    prose: ['body'],
    exempt: {
        channel: 'a member of NOTIFICATION_CHANNELS, checked by the schema',
        locale: 'a member of NOTIFICATION_LOCALES, checked by the schema',
        basedOn: 'a version number',
        note: 'stored only in the config.notification_templates.amended event payload; no view renders it',
    },
});
/**
 * The log's own query: how many rows, and which status.
 *
 * `limit` is bounded and defaulted here rather than held as a constant in the service, because the
 * console sends its own — every Ops list does (`opsBoardSchema`), and two unshared numbers is how a
 * page claims completeness its server never promised: `mayHoldMore` compares the rows it got
 * against the limit it asked for, so a server capping lower would never trip it.
 *
 * `status` is deliberately **not** a zod enum. A vocabulary is a domain refusal, and
 * `status_unknown` says in the screen's own words what `invalid_enum_value` does not.
 */
export const notificationSendsQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(100),
    status: z.string().optional(),
});
/**
 * Why the sends log refused something. One list for the surface rather than one per act
 * (`BILLING_ISSUE_CODES`' shape), so the gate that requires copy has one thing to read.
 *
 * `send_was_refused` is separate from `send_not_abandoned` because the remedy differs. A refused
 * row was stopped by our own send scan and **keeps nothing it refused** — `variables = '{}'` by
 * CHECK (migration 0065, I1) — so there is no message left to send and re-queueing one would
 * deliver an empty rendering. What is wrong there is the record the scan objected to, not the
 * delivery.
 */
export const NOTIFICATION_SEND_ISSUE_CODES = [
    // A status the vocabulary does not hold. Refused rather than answered with an empty page, because
    // a typo that reads as "nothing failed" is the quiet failure a filter has.
    'status_unknown',
    // Still queued, or already gone. Neither needs a person.
    'send_not_abandoned',
    // Refused by the send scan: nothing was kept, so nothing can be re-sent.
    'send_was_refused',
];
//# sourceMappingURL=notifications.js.map